import { useQueryParams } from 'hooks/useQueryParams'
import { intersection } from 'lodash'
import qs from 'qs'
import { useHistory, useLocation } from 'react-router-dom'

export type TypeFilter = 'approvals' | 'standard' | 'read_receipts' | 'todos'
export type Filters = {
  role: 'assignee' | 'creator'
  types: TypeFilter[]
  request_state: 'open' | 'closed'
  review_state: 'pending' | 'submitted'
  todo_state: 'incomplete' | 'completed'
}

type FilterSetter = <K extends keyof Filters>(filter: K, value: Filters[K]) => void

export const useFilters = (): {
  setValue: FilterSetter
  setValues: (filters: Partial<Filters>) => void
  values: Filters
} => {
  const { search } = useLocation()
  const history = useHistory()

  const { role, types, request_state, review_state, todo_state } = useQueryParams({
    role: (value) => (['assignee', 'creator'].includes(value) ? value : 'assignee'),
    types: (value) => intersection(value.split(','), ['approvals', 'standard', 'read_receipts', 'todos']),
    request_state: (value) => (['open', 'closed'].includes(value) ? value : 'open'),
    review_state: (value) => (['pending', 'submitted'].includes(value) ? value : 'pending'),
    todo_state: (value) => (['incomplete', 'completed'].includes(value) ? value : 'incomplete'),
  })

  const setValues = (filters: Partial<Filters>) => {
    history.replace({
      search: qs.stringify(
        {
          ...qs.parse(search, { ignoreQueryPrefix: true }),
          ...filters,
        },
        {
          indices: false,
          arrayFormat: 'comma',
        },
      ),
    })
  }

  const setValue: FilterSetter = (filter, value) => setValues({ [filter]: value })

  return {
    setValue,
    setValues,
    values: {
      role: role || 'assignee',
      types: types || [],
      request_state: request_state || 'open',
      review_state: review_state || 'pending',
      todo_state: todo_state || 'incomplete',
    },
  }
}
